import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { CheckCircle, ContentCut } from "@mui/icons-material";
import ThemeToggle from "@/components/ThemeToggle";
import { formatCents, formatDateOnly } from "@/lib/utils";
import { publicBookingRequest } from "@/lib/queryClient";
import type { BookingWithDetails } from "@shared/schema";

export default function BookingSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [booking, setBooking] = useState<BookingWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const token = params.get("token");

    if (!sessionId || !token) {
      setError(t("bookingSuccess.missingPayment"));
      setLoading(false);
    } else {
      // Not in swagger.json — requires a payment/Stripe extension on the booking API or gateway.
      (async () => {
        try {
          const res = await publicBookingRequest(
            "GET",
            `/api/public/booking/verify-payment?session_id=${encodeURIComponent(sessionId)}&token=${encodeURIComponent(token)}`,
          );
          const data = await res.json();
          if (cancelled) return;
          if (data.message) {
            setError(data.message);
          } else {
            setBooking(data);
          }
        } catch {
          if (!cancelled) setError(t("bookingSuccess.verifyFailed"));
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <Skeleton variant="circular" width={64} height={64} />
          <Skeleton width={192} height={24} />
          <Typography variant="body2" color="text.secondary">
            {t("bookingSuccess.verifying")}
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <Typography color="error">{error}</Typography>
          <Button onClick={() => navigate("/")} data-testid="button-back-home">
            {t("bookingSuccess.backToBooking")}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar sx={{ maxWidth: 672, mx: "auto", width: "100%", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "primary.contrastText",
              }}
            >
              <ContentCut sx={{ fontSize: 18 }} />
            </Box>
            <Typography fontWeight={600}>{t("bookingSuccess.appBarTitle")}</Typography>
          </Box>
          <ThemeToggle />
        </Toolbar>
      </AppBar>
      <Box sx={{ maxWidth: 448, mx: "auto", px: 2, py: 8, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "success.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <CheckCircle sx={{ fontSize: 36, color: "success.dark" }} />
        </Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom data-testid="text-booking-confirmed">
          {t("bookingSuccess.heading")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          {t("bookingSuccess.paymentReceived")}
        </Typography>
        {booking && (
          <Card>
            <CardContent sx={{ textAlign: "left" }}>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("bookingSuccess.service")}
                  </Typography>
                  <Typography variant="body2" fontWeight={500} data-testid="text-confirmed-service">
                    {booking.service?.name}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("bookingSuccess.staff")}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {booking.staffMember?.displayName}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("bookingSuccess.date")}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatDateOnly(booking.startAt)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("bookingSuccess.price")}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCents(booking.priceCents || 0, booking.currency || "GBP")}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("bookingSuccess.payment")}
                  </Typography>
                  <Typography variant="body2" fontWeight={500} color="success.main">
                    {t("common.paid")}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}
        <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate("/")} data-testid="button-book-another">
          {t("bookingFlow.bookAnother")}
        </Button>
      </Box>
    </Box>
  );
}
